import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDslConfigOwnerTenantId } from '@de/providers/DslConfigOwnerContext';
import type { DataSourceStatus } from '@de/providers/DataSourceManager.types';
import { useItemScopeIndexPath } from '@de/providers/ItemScopeContext';
import { stableStringify } from '@de/utils/data-sources/dataSourceCache';
import {
  VISIBILITY_BREAKPOINT_MEDIA,
  type VisibilityBreakpointToken,
} from '@de/utils/binding-enhancer/visibility';
import { useHostSlotLoader } from './HostSlotLoaderContext';
import type {
  HostSlotHandle,
  HostSlotInputs,
  HostSlotLoader,
  HostSlotLoading,
  HostSlotOutcome,
  HostSlotOutputs,
} from './loader';

export type HostSlotBreakpoint = VisibilityBreakpointToken;

/** Handlers by output name; each receives the emitted payload. */
export type HostSlotOutputHandlers = Readonly<
  Record<string, ((payload?: unknown) => void) | undefined>
>;

export interface HostSlotReservedSizeEntry {
  /** Pixels, clamped to 0–2000. */
  readonly minHeight?: number;
  /** Width / height, clamped to 0.1–10. */
  readonly aspectRatio?: number;
}

/** `tablet` and `desktop` inherit each value from the breakpoint below. */
export type HostSlotReservedSize = Readonly<
  Partial<Record<HostSlotBreakpoint, HostSlotReservedSizeEntry>>
>;

export interface HostSlotProps {
  /**
   * Puck node id. Inside a `platform.dataRepeater` the row's index path is
   * appended to form the loader's `placementId`.
   */
  readonly id?: string;
  /** Registry slot name the loader resolves, e.g. `product.assistant`. */
  readonly slot?: string;
  readonly reservedSize?: HostSlotReservedSize;
  /** Collapse an empty fallback, only before the box has been in view. */
  readonly collapseWhenEmpty?: boolean;
  /** Fill trigger. Unrecognized values use `visible`. */
  readonly loading?: HostSlotLoading;
  /** Resolved input values. Read inside effects only, never rendered. */
  readonly inputs?: HostSlotInputs;
  /**
   * Lifecycle of the data sources the inputs read, forwarded by the binding
   * enhancer. While `pending`, a fired trigger waits to fill.
   */
  readonly dataSourceStatus?: DataSourceStatus;
  /**
   * True while any source the inputs read is in flight, forwarded by the
   * binding enhancer. `dataSourceStatus` reports `error` when one source
   * failed and another is still pending, so this is what holds the fill.
   */
  readonly sourcesPending?: boolean;
  /**
   * The `_action.<output>` handlers the binding enhancer built, by output
   * name. The loader runs one when a module emits that output.
   */
  readonly outputs?: HostSlotOutputHandlers;
  /** True when no fallback content was authored. */
  readonly fallbackEmpty?: boolean;
  /** The rendered fallback. */
  readonly children?: React.ReactNode;
}

const BREAKPOINT_ORDER: readonly HostSlotBreakpoint[] = [
  'mobile',
  'tablet',
  'desktop',
];
const MIN_HEIGHT_MAX = 2000;
const ASPECT_RATIO_MIN = 0.1;
const ASPECT_RATIO_MAX = 10;
const RESERVED_TENANT_IDS = new Set(['default', 'dsl-templates']);
const INTERACTION_EVENTS = ['pointerenter', 'focusin', 'touchstart'] as const;
const KEYBOARD_NAVIGATION_KEYS = new Set([
  'Tab',
  'ArrowDown',
  'ArrowUp',
  'PageDown',
  'PageUp',
  'Home',
  'End',
]);
const EMPTY_INPUTS: HostSlotInputs = Object.freeze({});
const BOX_STYLE: React.CSSProperties = { position: 'relative' };
const COLLAPSED_BOX_STYLE: React.CSSProperties = {
  position: 'relative',
  minHeight: 0,
  aspectRatio: 'auto',
};
/**
 * Laid out over the reserved box but invisible, so a module that measures
 * itself while mounting sees the box's width instead of zero.
 */
const PENDING_MOUNT_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  pointerEvents: 'none',
};

/** Visually hidden text that a screen reader still announces. */
const VISUALLY_HIDDEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};
/**
 * The loading state fills the reserved box without adding to its height, so
 * the box is the same size as it is for the fallback and the module.
 */
const LOADING_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

/**
 * Rules for the loading placeholder. The pulse is off under reduced motion.
 * Without JavaScript the loading state can never end, so the fallback, which
 * is in the markup but hidden while loading, is shown instead.
 */
function buildLoadingCss(className: string): string {
  return (
    `.${className} [data-de-host-slot-loading]{background:rgba(127,127,127,.12);border-radius:4px}` +
    `@media (prefers-reduced-motion:no-preference){.${className} [data-de-host-slot-loading]{animation:de-host-slot-pulse 1.4s ease-in-out infinite}}` +
    `@keyframes de-host-slot-pulse{0%,100%{opacity:1}50%{opacity:.5}}`
  );
}

function buildNoScriptCss(className: string): string {
  return `.${className} [data-de-host-slot-loading]{display:none}.${className} [data-de-host-slot-fallback][hidden]{display:block}`;
}

type ResolvedSize = { minHeight?: number; aspectRatio?: number };

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function clampMinHeight(value: unknown): number | undefined {
  const parsed = toFiniteNumber(value);
  if (parsed === undefined) return undefined;
  return Math.min(MIN_HEIGHT_MAX, Math.max(0, Math.round(parsed)));
}

export function clampAspectRatio(value: unknown): number | undefined {
  const parsed = toFiniteNumber(value);
  if (parsed === undefined) return undefined;
  return Math.min(ASPECT_RATIO_MAX, Math.max(ASPECT_RATIO_MIN, parsed));
}

export interface ReservedSizeCssOptions {
  /**
   * Write `min-height` only and ignore any `aspectRatio`, so the box grows
   * with content taller than the reserved space instead of being held to a
   * ratio.
   */
  readonly minHeightOnly?: boolean;
}

function readSizeEntry(
  value: unknown,
  { minHeightOnly = false }: ReservedSizeCssOptions = {},
): ResolvedSize {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const entry = value as Record<string, unknown>;
  return {
    minHeight: clampMinHeight(entry.minHeight),
    aspectRatio: minHeightOnly
      ? undefined
      : clampAspectRatio(entry.aspectRatio),
  };
}

/**
 * Reserved-box CSS for every breakpoint. Pure `@media` rules, so server and
 * client output agree without reading the viewport in JS. `tablet` and
 * `desktop` inherit each value from the breakpoint below.
 */
export function buildReservedSizeCss(
  className: string,
  reservedSize: unknown,
  options: ReservedSizeCssOptions = {},
): string {
  const sizes =
    reservedSize !== null &&
    typeof reservedSize === 'object' &&
    !Array.isArray(reservedSize)
      ? (reservedSize as Record<string, unknown>)
      : {};
  const rules: string[] = [];
  let inherited: ResolvedSize = {};
  for (const breakpoint of BREAKPOINT_ORDER) {
    const own = readSizeEntry(sizes[breakpoint], options);
    inherited = {
      minHeight: own.minHeight ?? inherited.minHeight,
      aspectRatio: own.aspectRatio ?? inherited.aspectRatio,
    };
    const declarations: string[] = [];
    if (inherited.minHeight !== undefined) {
      declarations.push(`min-height:${inherited.minHeight}px`);
    }
    if (inherited.aspectRatio !== undefined) {
      declarations.push(`aspect-ratio:${inherited.aspectRatio}`);
    }
    if (declarations.length === 0) continue;
    rules.push(
      `@media ${VISIBILITY_BREAKPOINT_MEDIA[breakpoint]}{.${className}{${declarations.join(';')}}}`,
    );
  }
  return rules.join('');
}

function normalizeLoading(value: unknown): HostSlotLoading {
  return value === 'eager' || value === 'interaction' ? value : 'visible';
}

/**
 * Drops inputs that resolved to `undefined`. Everything else, `''` and `null`
 * included, reaches the loader, which owns what "no value" means per type.
 */
function normalizeInputs(value: unknown): HostSlotInputs {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_INPUTS;
  }
  return Object.fromEntries(
    Object.entries(value).filter(([, input]) => input !== undefined),
  );
}

function readFlowTenantId(loader: HostSlotLoader): unknown {
  try {
    return loader.flowTenantId;
  } catch {
    return undefined;
  }
}

/**
 * Fill only when both ids are present, neither names a reserved tenant, and
 * they match. Default-tenant fallback configs and hosts with no source key
 * both resolve to `default`, so equality alone would fill them.
 */
export function isFillableTenantPair(
  configTenantId: unknown,
  flowTenantId: unknown,
): boolean {
  if (typeof configTenantId !== 'string' || typeof flowTenantId !== 'string') {
    return false;
  }
  const config = configTenantId.trim();
  const flow = flowTenantId.trim();
  if (config.length === 0 || flow.length === 0) return false;
  if (
    RESERVED_TENANT_IDS.has(config.toLowerCase()) ||
    RESERVED_TENANT_IDS.has(flow.toLowerCase())
  ) {
    return false;
  }
  return config === flow;
}

function safeReportOutcome(
  loader: HostSlotLoader,
  outcome: HostSlotOutcome,
): void {
  try {
    loader.reportOutcome?.(outcome);
  } catch {
    // A failing reporter must not take the fallback down with it.
  }
}

type SlotPhase = 'fallback' | 'filled';
type SlotView = 'loading' | 'fallback' | 'filled';

/**
 * Runtime for `platform.hostSlot`. Renders a reserved box with the authored
 * fallback on the server and the first client render, then asks the host's
 * loader to fill it on the client. Every loader call is contained: a throw, a
 * rejected `ready` or a failed `update` restores the fallback and unmounts
 * the handle.
 */
export function HostSlot({
  id,
  slot,
  reservedSize,
  collapseWhenEmpty,
  loading,
  inputs,
  dataSourceStatus,
  sourcesPending,
  outputs,
  fallbackEmpty,
  children,
}: HostSlotProps): React.ReactElement {
  const loader = useHostSlotLoader();
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const hasLoader = loader !== null;
  const flowTenantId = loader === null ? undefined : readFlowTenantId(loader);
  const ownerTenantId = useDslConfigOwnerTenantId();
  const itemIndexPath = useItemScopeIndexPath();
  const className = `de-host-slot-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const slotName = typeof slot === 'string' ? slot : '';
  const nodeId = typeof id === 'string' ? id : '';
  const placementId =
    itemIndexPath !== undefined && nodeId.length > 0
      ? `${nodeId}::${itemIndexPath}`
      : nodeId;
  const trigger = normalizeLoading(loading);
  const resolvedInputs = normalizeInputs(inputs);
  const inputsKey = stableStringify(resolvedInputs);
  const inputsRef = useRef(resolvedInputs);
  inputsRef.current = resolvedInputs;
  const inputsReady = sourcesPending !== true && dataSourceStatus !== 'pending';
  const inputsReadyRef = useRef(inputsReady);
  inputsReadyRef.current = inputsReady;
  const outputsRef = useRef(outputs);
  outputsRef.current = outputs;

  const boxRef = useRef<HTMLDivElement>(null);
  const fallbackRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  /** The fallback held focus when a fill started or when `ready` resolved. */
  const fallbackHadFocusRef = useRef(false);
  /** The fallback element that held focus when a fill started. */
  const focusedFallbackElementRef = useRef<HTMLElement | null>(null);
  const handleRef = useRef<HostSlotHandle | null>(null);
  const sentInputsKeyRef = useRef<string | undefined>(undefined);
  /** A fill whose trigger fired while its inputs were pending. */
  const deferredFillRef = useRef<(() => void) | null>(null);
  /** Fails the current handle; shared with the update effect. */
  const failRef = useRef<(() => void) | null>(null);
  const seenRef = useRef(false);

  const [phase, setPhase] = useState<SlotPhase>('fallback');
  const [failed, setFailed] = useState(false);
  /** The loader holds the placement on something that may not come. */
  const [waiting, setWaiting] = useState(false);
  /** An `interaction` slot's trigger has fired. */
  const [triggered, setTriggered] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const fillable =
    hasLoader && isFillableTenantPair(ownerTenantId, flowTenantId);

  // Keyed on the loader's presence and tenant, not its identity, so a host
  // that re-creates the loader object on render does not refill every slot.
  useEffect(() => {
    const mountLoader = loaderRef.current;
    if (!mountLoader) return;
    if (!fillable) {
      if (slotName.length > 0) {
        safeReportOutcome(mountLoader, {
          slot: slotName,
          placementId,
          reason: 'config-tenant-mismatch',
        });
      }
      return;
    }
    const box = boxRef.current;
    const container = mountRef.current;
    if (!box || !container || slotName.length === 0) return;

    let disposed = false;
    let retired = false;
    let handle: HostSlotHandle | null = null;
    let stopTrigger = () => {};
    let stopStatus = () => {};
    let stopFocusWatch = () => {};

    // Hiding the fallback while it loads drops focus to `<body>`, so the
    // element that held it is noted first. If the visitor then moves focus
    // elsewhere, the note is dropped so nothing takes it back from them.
    const captureFallbackFocus = () => {
      if (focusedFallbackElementRef.current !== null) return;
      const doc = box.ownerDocument;
      const active = doc.activeElement;
      if (
        !(active instanceof HTMLElement) ||
        fallbackRef.current?.contains(active) !== true
      ) {
        return;
      }
      focusedFallbackElementRef.current = active;
      fallbackHadFocusRef.current = true;
      const onFocusIn = (event: FocusEvent) => {
        const target = event.target;
        if (
          target instanceof Node &&
          fallbackRef.current?.contains(target) === true
        ) {
          return;
        }
        focusedFallbackElementRef.current = null;
        fallbackHadFocusRef.current = false;
        stopFocusWatch();
      };
      doc.addEventListener('focusin', onFocusIn, true);
      stopFocusWatch = () => {
        doc.removeEventListener('focusin', onFocusIn, true);
        stopFocusWatch = () => {};
      };
    };

    const release = () => {
      const current = handle;
      handle = null;
      handleRef.current = null;
      sentInputsKeyRef.current = undefined;
      stopStatus();
      stopStatus = () => {};
      stopFocusWatch();
      if (!current) return;
      try {
        current.unmount();
      } catch {
        // The handle is being discarded either way.
      }
    };

    const fail = () => {
      if (disposed || retired) return;
      retired = true;
      stopTrigger();
      deferredFillRef.current = null;
      release();
      setPhase('fallback');
      setWaiting(false);
      setFailed(true);
    };
    failRef.current = fail;

    const readHandler = (output: string) => {
      const handlers = outputsRef.current;
      if (
        handlers === undefined ||
        !Object.prototype.hasOwnProperty.call(handlers, output)
      ) {
        return undefined;
      }
      const handler = handlers[output];
      return typeof handler === 'function' ? handler : undefined;
    };
    // One port per effect run, not per render: the loader keeps it for the
    // life of the placement, and the handlers behind it are rebuilt on every
    // render, so each call reads the newest ones.
    const outputsPort: HostSlotOutputs = {
      has: (output) => readHandler(output) !== undefined,
      run: (output, payload) => {
        if (disposed || retired) return;
        const handler = readHandler(output);
        if (!handler) return;
        try {
          handler(payload);
        } catch {
          // A failing action must not reach the module or the page.
        }
      },
    };

    const fill = () => {
      if (disposed || retired || handle) return;
      captureFallbackFocus();
      setTriggered(true);
      if (!inputsReadyRef.current) {
        deferredFillRef.current = fill;
        return;
      }
      deferredFillRef.current = null;
      let filled: HostSlotHandle | null;
      try {
        filled = (loaderRef.current ?? mountLoader).fill({
          slot: slotName,
          placementId,
          container,
          configTenantId: (ownerTenantId ?? '').trim(),
          context: { inputs: inputsRef.current },
          outputs: outputsPort,
        });
      } catch {
        fail();
        return;
      }
      if (!filled) {
        fail();
        return;
      }
      handle = filled;
      handleRef.current = filled;
      sentInputsKeyRef.current = stableStringify(inputsRef.current);
      const status = filled.status;
      if (status) {
        const applyStatus = () => {
          if (disposed || handle !== filled) return;
          try {
            setWaiting(status.read() === 'waiting');
          } catch {
            setWaiting(false);
          }
        };
        try {
          const unsubscribe = status.subscribe(applyStatus);
          stopStatus = () => {
            try {
              unsubscribe();
            } catch {
              // The subscription is being dropped either way.
            }
          };
        } catch {
          // Without a subscription the status is read once, below.
        }
        applyStatus();
      }
      let ready: Promise<void>;
      try {
        ready = Promise.resolve(filled.ready);
      } catch {
        fail();
        return;
      }
      ready.then(
        () => {
          if (disposed || handle !== filled) return;
          const active = container.ownerDocument.activeElement;
          fallbackHadFocusRef.current =
            fallbackHadFocusRef.current ||
            (active !== null && fallbackRef.current?.contains(active) === true);
          stopFocusWatch();
          setPhase('filled');
        },
        () => {
          if (handle === filled) fail();
        },
      );
      let released: Promise<void> | undefined;
      try {
        released = filled.released
          ? Promise.resolve(filled.released)
          : undefined;
      } catch {
        released = undefined;
      }
      released?.then(
        () => {
          if (handle === filled) fail();
        },
        () => {
          // A broken release signal must not take the placement down.
        },
      );
    };

    if (trigger === 'eager') {
      fill();
    } else if (trigger === 'visible') {
      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          fill();
        });
        observer.observe(box);
        stopTrigger = () => observer.disconnect();
      } else {
        // Nothing will ever fill this slot, so it must not keep loading.
        setFailed(true);
      }
    } else {
      const doc = box.ownerDocument;
      let keyboardObserver: IntersectionObserver | null = null;
      const onFirstInteraction = () => {
        stopTrigger();
        fill();
      };
      // A box without focusable content never receives `focusin`, so once
      // the visitor navigates by keyboard the slot fills when it is in view.
      const onKeyboardNavigation = (event: KeyboardEvent) => {
        if (!KEYBOARD_NAVIGATION_KEYS.has(event.key)) return;
        doc.removeEventListener('keydown', onKeyboardNavigation, true);
        if (typeof IntersectionObserver === 'undefined') {
          onFirstInteraction();
          return;
        }
        keyboardObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            onFirstInteraction();
          }
        });
        keyboardObserver.observe(box);
      };
      for (const eventName of INTERACTION_EVENTS) {
        box.addEventListener(eventName, onFirstInteraction, { passive: true });
      }
      doc.addEventListener('keydown', onKeyboardNavigation, true);
      stopTrigger = () => {
        for (const eventName of INTERACTION_EVENTS) {
          box.removeEventListener(eventName, onFirstInteraction);
        }
        doc.removeEventListener('keydown', onKeyboardNavigation, true);
        keyboardObserver?.disconnect();
      };
    }

    return () => {
      disposed = true;
      stopTrigger();
      deferredFillRef.current = null;
      if (failRef.current === fail) failRef.current = null;
      release();
      focusedFallbackElementRef.current = null;
      fallbackHadFocusRef.current = false;
      setPhase('fallback');
      setFailed(false);
      setWaiting(false);
      setTriggered(false);
    };
  }, [
    hasLoader,
    flowTenantId,
    fillable,
    ownerTenantId,
    slotName,
    placementId,
    trigger,
  ]);

  useEffect(() => {
    if (inputsReady) deferredFillRef.current?.();
  }, [inputsReady]);

  // Removing a focused fallback drops focus to `<body>`; hand it to the mount
  // container unless the visitor already moved it elsewhere.
  useEffect(() => {
    if (phase !== 'filled' || !fallbackHadFocusRef.current) return;
    fallbackHadFocusRef.current = false;
    focusedFallbackElementRef.current = null;
    const container = mountRef.current;
    if (!container) return;
    const doc = container.ownerDocument;
    if (doc.activeElement !== null && doc.activeElement !== doc.body) return;
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    container.focus({ preventScroll: true });
  }, [phase]);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle || !inputsReady || sentInputsKeyRef.current === inputsKey) {
      return;
    }
    sentInputsKeyRef.current = inputsKey;
    try {
      handle.update({ inputs: inputsRef.current });
    } catch {
      failRef.current?.();
    }
  }, [inputsKey, inputsReady]);

  const fallbackIsFinal =
    loader === null || !fillable || slotName.length === 0 || failed;
  const mayCollapse = collapseWhenEmpty === true && fallbackEmpty === true;
  const canCollapse = mayCollapse && fallbackIsFinal && phase === 'fallback';

  // Tracks visibility from mount, independently of the fill, so a box seen
  // while a fill was pending never collapses after that fill fails.
  useEffect(() => {
    if (!mayCollapse || seenRef.current) return;
    const box = boxRef.current;
    if (!box || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      seenRef.current = true;
      observer.disconnect();
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, [mayCollapse]);

  useEffect(() => {
    if (!canCollapse || seenRef.current) return;
    const box = boxRef.current;
    if (!box || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      observer.disconnect();
      if (entry.isIntersecting || seenRef.current) {
        seenRef.current = true;
        return;
      }
      setCollapsed(true);
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, [canCollapse]);

  const css = useMemo(
    () => buildReservedSizeCss(className, reservedSize),
    [className, reservedSize],
  );
  const isFilled = phase === 'filled';
  const isCollapsed = collapsed && canCollapse;
  const awaitingTrigger = trigger === 'interaction' && !triggered;
  const view: SlotView = isFilled
    ? 'filled'
    : fillable && slotName.length > 0 && !awaitingTrigger && !failed && !waiting
      ? 'loading'
      : 'fallback';
  const isLoading = view === 'loading';

  // A fill that ends without a module hands focus back to the fallback
  // element that held it, unless the visitor has moved it since.
  useEffect(() => {
    if (view !== 'fallback') return;
    const element = focusedFallbackElementRef.current;
    if (element === null) return;
    focusedFallbackElementRef.current = null;
    fallbackHadFocusRef.current = false;
    const doc = element.ownerDocument;
    if (!element.isConnected) return;
    if (doc.activeElement !== null && doc.activeElement !== doc.body) return;
    element.focus({ preventScroll: true });
  }, [view]);

  return (
    <div
      ref={boxRef}
      className={className}
      data-de-host-slot={slotName}
      data-de-host-slot-state={view}
      data-de-host-slot-collapsed={isCollapsed ? '' : undefined}
      style={isCollapsed ? COLLAPSED_BOX_STYLE : BOX_STYLE}
    >
      {css || isLoading ? (
        <style>{isLoading ? css + buildLoadingCss(className) : css}</style>
      ) : null}
      {isLoading ? (
        <div
          data-de-host-slot-loading=''
          role='status'
          aria-busy='true'
          style={LOADING_STYLE}
        >
          <span style={VISUALLY_HIDDEN_STYLE}>Loading</span>
        </div>
      ) : null}
      {isLoading ? (
        <noscript>
          <style>{buildNoScriptCss(className)}</style>
        </noscript>
      ) : null}
      {isFilled ? null : (
        <div
          ref={fallbackRef}
          data-de-host-slot-fallback=''
          hidden={isLoading ? true : undefined}
        >
          {children}
        </div>
      )}
      <div
        ref={mountRef}
        data-de-host-slot-mount=''
        style={isFilled ? undefined : PENDING_MOUNT_STYLE}
      />
    </div>
  );
}
